import {
  EntityType,
  type UploadMediaCommandInput,
  type UploadMediaCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  uploadDescriptionImages,
  type UploadDescriptionImagesContext,
} from '../../../shared/data';
import type {
  QuestionContent,
  MediaIdsMap,
  SubQuestion,
} from 'src/types/helios/Question';
import type { CriteriaAttachments } from '../components/AuditCriteriaStep';
import { uploadCriteriaAttachments } from './uploadCriteriaAttachments';

/** The five rich-text criteria fields on `questionDetails` whose inline images
 *  must be uploaded + rewritten. (title is a plain string — no media.) */
const CRITERIA_RICH_TEXT_FIELDS = [
  'questionText',
  'auditInstruction',
  'passCriteria',
  'failCriteria',
  'justification',
] as const;

type CriteriaRichTextField = (typeof CRITERIA_RICH_TEXT_FIELDS)[number];

export interface AssembleQuestionMediaResult {
  /** Content with every rich-text field's base64 images rewritten to media-id
   *  `src`s. Safe to persist (no raw base64 bloat). */
  readonly content: QuestionContent;
  /** The question-level media-id map (criteria fields + task description),
   *  merging inline-image ids and discrete-attachment ids per field. */
  readonly mediaIdsMap: MediaIdsMap;
}

/** Merges two field→ids maps, concatenating ids per field (inline first, then
 *  attachments) and de-duplicating. Pure; neither input is mutated. */
function mergeMediaIdsMaps(a: MediaIdsMap, b: MediaIdsMap): MediaIdsMap {
  const merged = new Map<string, string[]>();
  for (const source of [a, b]) {
    for (const [field, ids] of Object.entries(source)) {
      if (!ids) continue;
      const existing = merged.get(field) ?? [];
      merged.set(field, Array.from(new Set([...existing, ...ids])));
    }
  }
  const result: Record<string, string[]> = Object.fromEntries(merged);
  return result;
}

/**
 * Uploads all media for a question being published and returns persistence-safe
 * content + the assembled `mediaIdsMap`.
 *
 * For every rich-text field (the five criteria fields and the optional task
 * description), inline base64 images are uploaded via `uploadDescriptionImages`
 * (which also rewrites the HTML to media-id `src`s). Discrete attachment files
 * per criteria field are uploaded via `uploadCriteriaAttachments`. The two
 * id maps are merged per field. Mirrors the manual flow's media assembly,
 * minus the legacy presigned-url handling (Astra drafts are net-new).
 *
 * Sub-question media is assembled separately by {@link assembleSubQuestionMedia}
 * so each sub carries its own `mediaIdsMap`.
 */
export async function assembleQuestionMedia(
  content: QuestionContent,
  attachments: CriteriaAttachments,
  context: UploadDescriptionImagesContext,
  uploadMedia: (input: UploadMediaCommandInput) => Promise<UploadMediaCommandOutput>,
): Promise<AssembleQuestionMediaResult> {
  const questionImageOptions = { entityType: EntityType.QUESTION };

  // 1. Inline images in each criteria rich-text field — upload + rewrite,
  //    keyed by the field name.
  const criteriaResults = await Promise.all(
    CRITERIA_RICH_TEXT_FIELDS.map(async (field) => {
      const html = readCriteriaField(content, field);
      const result = await uploadDescriptionImages(html, context, uploadMedia, {
        ...questionImageOptions,
        fieldKey: field,
      });
      return { field, description: result.description, map: result.taskMediaIdsMap };
    }),
  );

  // 2. Inline images in the optional task description.
  const taskDescription = content.taskDetails?.taskDescription ?? '';
  const taskResult = await uploadDescriptionImages(
    taskDescription,
    context,
    uploadMedia,
    { ...questionImageOptions, fieldKey: 'taskDescription' },
  );

  // 3. Discrete attachment files per criteria field.
  const attachmentsMap = await uploadCriteriaAttachments(
    attachments,
    context,
    uploadMedia,
  );

  // Rebuild content with rewritten HTML (criteria + task description). Read
  // each rewritten field explicitly (no variable-keyed object write) so the
  // object-injection sink never fires.
  const rewrittenByField = new Map(criteriaResults.map((r) => [r.field, r.description]));
  const rewrittenContent: QuestionContent = {
    ...content,
    questionDetails: {
      ...content.questionDetails,
      questionText:
        rewrittenByField.get('questionText') ?? content.questionDetails.questionText,
      auditInstruction:
        rewrittenByField.get('auditInstruction') ??
        content.questionDetails.auditInstruction,
      passCriteria:
        rewrittenByField.get('passCriteria') ?? content.questionDetails.passCriteria,
      failCriteria:
        rewrittenByField.get('failCriteria') ?? content.questionDetails.failCriteria,
      justification:
        rewrittenByField.get('justification') ?? content.questionDetails.justification,
    },
    ...(content.taskDetails
      ? {
          taskDetails: {
            ...content.taskDetails,
            taskDescription: taskResult.description,
          },
        }
      : {}),
  };

  // Merge inline-image maps (criteria + task) with the discrete-attachment map.
  const inlineMap = criteriaResults.reduce<MediaIdsMap>(
    (acc, r) => mergeMediaIdsMaps(acc, r.map),
    {},
  );
  const inlinePlusTask = mergeMediaIdsMaps(inlineMap, taskResult.taskMediaIdsMap);
  const mediaIdsMap = mergeMediaIdsMaps(inlinePlusTask, attachmentsMap);

  return { content: rewrittenContent, mediaIdsMap };
}

/** Reads a criteria rich-text field off `questionDetails` without a dynamic
 *  object-index sink (the field is a known literal from the fixed list). */
function readCriteriaField(
  content: QuestionContent,
  field: CriteriaRichTextField,
): string {
  const details = content.questionDetails;
  switch (field) {
    case 'questionText':
      return details.questionText;
    case 'auditInstruction':
      return details.auditInstruction;
    case 'passCriteria':
      return details.passCriteria;
    case 'failCriteria':
      return details.failCriteria;
    case 'justification':
      return details.justification;
  }
}

/**
 * Uploads inline images for one sub-question's `instruction` and optional
 * `taskDetails.taskDescription`, returning the sub with rewritten HTML and its
 * own `mediaIdsMap` (keyed `instruction` / `taskDescription`, matching the
 * manual flow's per-sub map). The legacy flat `mediaIds` field is dropped.
 */
export async function assembleSubQuestionMedia(
  sub: SubQuestion,
  context: UploadDescriptionImagesContext,
  uploadMedia: (input: UploadMediaCommandInput) => Promise<UploadMediaCommandOutput>,
): Promise<SubQuestion> {
  const questionImageOptions = { entityType: EntityType.QUESTION };

  const instructionResult = await uploadDescriptionImages(
    sub.instruction,
    context,
    uploadMedia,
    { ...questionImageOptions, fieldKey: 'instruction' },
  );

  const taskDescription = sub.taskDetails?.taskDescription ?? '';
  const taskResult = await uploadDescriptionImages(
    taskDescription,
    context,
    uploadMedia,
    {
      ...questionImageOptions,
      fieldKey: 'taskDescription',
    },
  );

  const subMediaIdsMap: { instruction?: string[]; taskDescription?: string[] } = {
    ...instructionResult.taskMediaIdsMap,
    ...taskResult.taskMediaIdsMap,
  };

  // Drop the deprecated flat `mediaIds` from the save payload (mediaIdsMap
  // supersedes it).
  const subWithoutLegacy: SubQuestion = { ...sub };
  delete subWithoutLegacy.mediaIds;

  return {
    ...subWithoutLegacy,
    instruction: instructionResult.description,
    ...(sub.taskDetails
      ? {
          taskDetails: {
            ...sub.taskDetails,
            taskDescription: taskResult.description,
          },
        }
      : {}),
    mediaIdsMap: subMediaIdsMap,
  };
}
